<template>
  <div class="register-area">
    <loader v-show="isFormLoading"></loader>
    <div v-show="!isFormLoading">
      <error :errors="errors"></error>
      <b-form @submit="onSubmit">
        <div class="pep-section">
          <div>
            <div>{{ $t('investorprofile.toBeCompliant') }}</div>
            <b-form-group>{{ $t('investorprofile.occupation') }}<div>
                <b-form-select v-model="form.occupation" :options="this.translate(occupation_options)" required></b-form-select>
                <div class="mt-3" v-if="form.occupation==='Other'">
                  <b-form-input v-model="form.other_occupation" :formatter="onlyLatinFormatter" required :placeholder="$t('investorprofile.enterYourOccupation')"></b-form-input>
                </div>
              </div>
            </b-form-group>
            <b-form-group>{{ $t('investorprofile.industry') }}<div>
                <b-form-select v-model="form.industry" :options="this.translate(industry_options)" required></b-form-select>
                <div class="mt-3" v-if="form.industry==='Other'">
                  <b-form-input v-model="form.other_industry" :formatter="onlyLatinFormatter" required :placeholder="$t('investorprofile.enterYourIndustry')"></b-form-input>
                </div>
              </div>
            </b-form-group>
            <div>
              <b-form-group>{{ $t('investorprofile.mainSourceOfFunds') }}<b-form-checkbox-group
                    v-model="form.msof"
                    :options="this.translate(msof_options)"
                    ref="msof"
                    stacked
                >
                </b-form-checkbox-group>
              </b-form-group>
            </div>
            <div>
              <b-form-group>{{ $t('investorprofile.iConfirmThat') }}
                <help-tooltip tooltipText="investorprofile.iConfirmThatTooltip"></help-tooltip>
                <b-form-radio-group id="radio-group-actual-beneficiary" v-model="actual_beneficiary" name="radio-actual-beneficiary-component" required>
                  <b-form-radio name="actual_beneficiary" value="true">{{ $t('investorprofile.yes') }}</b-form-radio>
                  <b-form-radio name="actual_beneficiary" value="false">{{ $t('investorprofile.no') }}</b-form-radio>
                </b-form-radio-group>
              </b-form-group>
            </div>
            <div v-if="actual_beneficiary==='false'">
              <div>{{ $t('investorprofile.pleaseSpecifyThe') }}</div>
              <div class="inline-group">
                <b-form-group class="w-50 first-inline-field">
                  <b-form-input v-model="form.beneficial_owner.first_name" required :placeholder="$t('investorprofile.enterFirstName')" :formatter="onlyLatinFormatter">{{ $t('investorprofile.firstName') }}</b-form-input>
                </b-form-group>
                <b-form-group class="w-50 last-inline-field">
                  <b-form-input v-model="form.beneficial_owner.last_name" required :placeholder="$t('investorprofile.enterLastName')" :formatter="onlyLatinFormatter">{{ $t('investorprofile.lastName') }}</b-form-input>
                </b-form-group>
              </div>
              <b-form-group>{{ $t('investorprofile.dateOfBirth') }}<b-form-datepicker v-model="form.beneficial_owner.dob"
                                   :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric'}"
                                   :date-disabled-fn="dateDisabled"
                                   required
                                   locale="en">
                </b-form-datepicker>
              </b-form-group>

              <b-form-group class="form-group" v-if="citizenshipItems">{{ $t('investorprofile.citizenship') }}<cool-select
                    required
                    v-model="selectedCitizenship"
                    :items="citizenshipItems"
                    :placeholder="selectedCitizenship ? '' : $t('app.selectCountry')"
                    item-value="short_name"
                    :item-text="$t('investorprofile.name')"
                    aria-required="true"
                    ref="citizenship"
                    class="country-select form-field"
                    :class="submitted && !selectedCitizenship? ' form-control is-invalid': ''"
                >
                  <!-- slot for each item in the menu -->
                  <template slot="item" slot-scope="{ item: country }">
                    <div style="display: flex; align-items: center;">
                      <img v-if="country.short_name" :src="getFlag(country.short_name)" class="country-flag">

                      <div>
                        <b>{{ country.name }}</b>
                      </div>
                    </div>
                  </template>
                  <!-- slot for the selected item (in the text field) -->
                  <template slot="selection" slot-scope="{ item: country }">
                    <img v-if="country.short_name" :src="getFlag(country.short_name)" class="country-flag">
                    <div>
                      <b>{{ country.name }}</b>
                    </div>
                  </template>
                </cool-select>
              </b-form-group>
            </div>
            <div class="according">{{ $t('investorprofile.areYouOne') }}<img alt="" class="help" id="pep_help" v-bind:src="getSrc('help.svg')" @click="pep_help = !pep_help"/>
              <b-alert v-model="pep_help" variant="info" dismissible>
                <div>{{ $t('investorprofile.politicallyExposedPersonsPeps') }}</div>
                <br/>
                <div><strong>{{ $t('investorprofile.aPoliticallyExposedPerson') }}</strong>{{ $t('investorprofile.isANatural') }}</div>
                <br/>
                <div><strong>{{ $t('investorprofile.immediateFamilyMembersInclude') }}</strong>:</div>
                <div>{{ $t('investorprofile.aSpouseOr') }}</div>
                <div>{{ $t('investorprofile.bAChild') }}</div>
                <div>{{ $t('investorprofile.cAParent') }}</div>
                <div>{{ $t('investorprofile.dABrother') }}</div>
                <br/>
                <div><strong>{{ $t('investorprofile.closeAssociates') }}</strong>{{ $t('investorprofile.includeIndividualsWho') }}</div>
              </b-alert>
            </div>
            <b-form-group>
              <b-form-radio-group id="radio-group-pep-enabled" v-model="pep_enabled" name="radio-pep-component" required>
                <b-form-radio name="pep_enabled" value="true">{{ $t('investorprofile.yes1') }}</b-form-radio>
                <b-form-radio name="pep_enabled" value="false">{{ $t('investorprofile.no') }}</b-form-radio>
              </b-form-radio-group>
            </b-form-group>
            <div v-if="pep_enabled==='true'">
              <b-form-radio-group id="radio-group-pep-type" v-model="form.pep_frontend.type" name="radio-pep-type" required>
                <b-form-radio class="w-100" name="pep_type" value="iam">{{ $t('investorprofile.yesIAm') }}</b-form-radio>
                <b-form-radio class="w-100" name="pep_type" value="family">{{ $t('investorprofile.someOfMy') }}</b-form-radio>
                <b-form-radio class="w-100" name="pep_type" value="close">{{ $t('investorprofile.someOfMy1') }}</b-form-radio>
              </b-form-radio-group>
              <div v-if="form.pep_frontend.type" class="pep-type-form">
                <b-form-group class="form-group">
                  <b-form-input class="form-field"
                                v-model="form.pep_frontend.position"
                                required
                                :placeholder="$t('investorprofile.pleaseSpecifyPosition')"
                                trim
                                :formatter="onlyLatinFormatter"
                  ></b-form-input>
                </b-form-group>
                <b-form-group class="form-group">
                  <b-form-input class="form-field"
                                v-model="form.pep_frontend.organization"
                                required
                                :placeholder="$t('investorprofile.organization')"
                                trim
                                :formatter="onlyLatinFormatter"
                  ></b-form-input>
                </b-form-group>
                <div v-if="form.pep_frontend.type !== 'iam' ">
                  <b-form-group class="form-group">
                    <b-form-input class="form-field"
                                  v-model="form.pep_frontend.relationship"
                                  required
                                  :placeholder="$t('investorprofile.relationship')"
                                  trim
                                  :formatter="onlyLatinFormatter"
                    ></b-form-input>
                  </b-form-group>
                </div>
                <b-form-group class="form-group" v-if="countryItems">
                  <cool-select
                      required
                      v-model="selectedCountry"
                      :items="countryItems"
                      :placeholder="selectedCountry ? '' : $t('app.selectCountry')"
                      item-value="short_name"
                      :item-text="$t('investorprofile.name1')"
                      aria-required="true"
                      ref="country"
                      class="country-select form-field"
                      :class="submitted && !selectedCountry? ' form-control is-invalid': ''"
                  >
                    <!-- slot for each item in the menu -->
                    <template slot="item" slot-scope="{ item: country }">
                      <div style="display: flex; align-items: center;">
                        <img v-if="country.short_name" :src="getFlag(country.short_name)" class="country-flag">

                        <div>
                          <b>{{ country.name }}</b>
                        </div>
                      </div>
                    </template>
                    <!-- slot for the selected item (in the text field) -->
                    <template slot="selection" slot-scope="{ item: country }">
                      <img v-if="country.short_name" :src="getFlag(country.short_name)" class="country-flag">
                      <div>
                        <b>{{ country.name }}</b>
                      </div>
                    </template>
                  </cool-select>
                </b-form-group>
              </div>
            </div>
            <b-form-group>
              <b-form-checkbox name="agreement" v-model="form.investor_profile_agreement" class="confirm-agreement" required>{{ $t('investorprofile.herebyIConfirm') }}</b-form-checkbox>
            </b-form-group>
          </div>
        </div>
        <div class="veriff-section" v-show="pep_enabled==='false' || this.submitted">
        </div>
        <div class="w-100" style="display: inline-block">
          <div class="text-right w-100" style="display: inline-block">
            <b-button type="submit" class="income-btn register-btn" variant="primary">{{ $t('investorprofile.confirm') }}</b-button>
          </div>
        </div>
      </b-form>
    </div>
  </div>
</template>

<script>
import {CoolSelect} from 'vue-cool-select'
import Error from './Error'
import investor from '@/api/investor'
import flagMixin from '@/mixins/flag'
import Loader from '@/components/Loader'
import imageMixin from '@/mixins/image'
import HelpTooltip from '@/components/HelpTooltip'
import textMixin from '../mixins/text'
import classificatorDataHelper from '@/helpers/classificatorDataHelper'

export default {
  components: {Loader, Error, CoolSelect, HelpTooltip},
  mixins: [flagMixin, imageMixin, textMixin],
  data: () => ({
    submitted: false,
    isFormLoading: false,
    decision_message: 'Person has not been verified.',
    reason_message: 'Not verified',
    errors: [],
    identity_status: 'Not verified',
    pep_enabled: null,
    actual_beneficiary: null,
    pep_help: false,
    selectedCountry: null,
    selectedCitizenship: null,
    form: {
      occupation: '',
      other_occupation: '',
      industry: '',
      other_industry: '',
      msof: null,
      pep_frontend: {
        type: '',
        position: '',
        organization: '',
        relationship: '',
        country: ''
      },
      beneficial_owner: {
        first_name: '',
        last_name: '',
        dob: '2000-01-01',
        citizenship: ''
      },
      investor_profile_agreement: false,
      submitted_confirm: true
    },
    occupation_options: [],
    industry_options: [],
    msof_options: [],
    countryItems: null,
    citizenshipItems: null,
    investor_other_info: null,
    investor_pep_frontend_content: null
  }),
  computed: {},
  mounted()
  {
    this.citizenshipItems = this.countryItems = classificatorDataHelper.getCountriesWithPopularInTop()
    if (this.$store.state.investors.investor)
    {
      this.occupation_options = classificatorDataHelper.getGroupItemsAsOptions('OCCUPATION')
      this.industry_options = classificatorDataHelper.getGroupItemsAsOptions('OCCUPATION_INDUSTRY')
      this.msof_options = classificatorDataHelper.getGroupItemsAsOptions('INDIVIDUAL_MAIN_SOURCE_OF_FUNDS')
      const investor = this.$store.state.investors.investor
      if (investor.investor_other_info)
      {
        this.investor_other_info = JSON.parse(investor.investor_other_info)
        // console.log(this.investor_other_info)
        this.form.occupation = this.investor_other_info.occupation ? this.investor_other_info.occupation : this.form.occupation
        this.form.other_occupation = this.investor_other_info.other_occupation ? this.investor_other_info.other_occupation : this.form.other_occupation
        this.form.industry = this.investor_other_info.industry ? this.investor_other_info.industry : this.form.industry
        this.form.msof = this.investor_other_info.msof ? this.investor_other_info.msof : this.form.msof
        this.form.other_industry = this.investor_other_info.other_industry
        this.form.investor_profile_agreement = this.investor_other_info.investor_profile_agreement
        if (this.investor_other_info.beneficial_owner && this.investor_other_info.beneficial_owner.first_name)
        {
          this.actual_beneficiary = 'false'
          this.form.beneficial_owner.first_name = this.investor_other_info.beneficial_owner.first_name ? this.investor_other_info.beneficial_owner.first_name : ''
          this.form.beneficial_owner.last_name = this.investor_other_info.beneficial_owner.last_name ? this.investor_other_info.beneficial_owner.last_name : ''
          this.form.beneficial_owner.dob = this.investor_other_info.beneficial_owner.dob ? this.investor_other_info.beneficial_owner.dob : this.form.beneficial_owner.dob
          this.selectedCitizenship = this.investor_other_info.beneficial_owner.citizenship ? this.investor_other_info.beneficial_owner.citizenship : this.selectedCitizenship
        } else if (this.investor_other_info.occupation) {
          // check this.investor_other_info.occupation that not empty {} for new investors
          this.actual_beneficiary = 'true'
        }
      }
      if (investor.investor_pep_frontend === 0) {
        this.pep_enabled = 'false'
      }
      if (investor.investor_pep_frontend_content)
      {
        this.investor_pep_frontend_content = JSON.parse(investor.investor_pep_frontend_content)
        // console.log(this.investor_pep_frontend_content)
        if (this.investor_pep_frontend_content.type)
        {
          this.pep_enabled = 'true'
          this.form.pep_frontend.type = this.investor_pep_frontend_content.type ? this.investor_pep_frontend_content.type : this.form.pep_frontend.type
          this.selectedCountry = this.investor_pep_frontend_content.country ? this.investor_pep_frontend_content.country : this.selectedCountry
          this.form.pep_frontend.organization = this.investor_pep_frontend_content.organization ? this.investor_pep_frontend_content.organization : this.form.pep_frontend.organization
          this.form.pep_frontend.position = this.investor_pep_frontend_content.position ? this.investor_pep_frontend_content.position : this.form.pep_frontend.position
          this.form.pep_frontend.relationship = this.investor_pep_frontend_content.relationship ? this.investor_pep_frontend_content.relationship : this.form.pep_frontend.relationship
        }
        else
        {
          this.pep_enabled = 'false'
        }
      }
    }
  },
  watch: {
    selectedCountry: function (selected)
    {
      this.form.pep_frontend.country = selected
    },
    'form.msof': function (val)
    {
      if (val.length > 0)
      {
        this.$refs.msof.$el.classList.remove('invalid', 'is-invalid')
      }
      else
      {
        this.$refs.msof.$el.classList.add('invalid', 'is-invalid')
      }
    }
  },
  methods: {
    onSubmit(evt)
    {
      evt.preventDefault()
      // console.log(this.pep_enabled)
      const bufOfBeneficialOwner = this.form.beneficial_owner
      if (this.pep_enabled === 'true')
      {
        if (!this.form.pep_frontend.country)
        {
          this.$refs.country.$el.classList.add('form-control', 'is-invalid')
          return false
        }
        else
        {
          this.$refs.country.$el.classList.remove('form-control', 'is-invalid')
        }
      } else {
        this.form.pep_frontend = false
      }
      if (this.actual_beneficiary === 'false')
      {
        if (!this.selectedCitizenship)
        {
          this.$refs.citizenship.$el.classList.add('form-control', 'is-invalid')
          return false
        }
        else
        {
          this.form.beneficial_owner.citizenship = this.selectedCitizenship
          this.$refs.citizenship.$el.classList.remove('form-control', 'is-invalid')
        }
      } else {
        this.form.beneficial_owner = false
      }
      if ((this.form.msof && this.form.msof.length < 1) || this.form.msof === null)
      {
        this.$refs.msof.$el.classList.add('invalid', 'is-invalid')
        this.$refs.msof.$el.focus()
        this.errors.push(this.$t('investorprofile.chooseSourceError'))
        return false
      }
      this.isFormLoading = true
      this.$emit('updateinvestor', this.form)
      investor.update(this.form).then((response) =>
          {
            if (response)
            {
              window.scrollTo(0, 0)
              // this.$store.state.investors.investor.investor_account_status = investorStatuses.PROFILE_FILLED
              // console.log('Set response.investor_account_status:', response.investor_account_status)
              this.$store.state.investors.investor.investor_account_status = response.investor_account_status
              this.$store.state.investors.investor.problems = response.problems
              this.$emit('setstep', 3)
              this.submitted = true
            }
          }
      ).catch((error) =>
      {
        this.errors.push(error)
        this.submitted = false
      }).then(() =>
          {
            this.isFormLoading = false
            this.form.beneficial_owner = bufOfBeneficialOwner
          }
      )
    },
    dateDisabled(ymd, date)
    {
      const now = new Date()
      const year = date.getFullYear() + 18
      return year > now.getFullYear()
    },
    translate(options)
    {
      return options.map(el =>
          {
            if (el.label_i18n)
            {
              el.text = this.$t(el.label_i18n)
            }
            return el
          }
      )
    }
  }
}
</script>

<style lang="scss" scoped>
.pep-section {
  display: inline-block;
  margin-top: 15px;
  margin-bottom: 2px;
  width: 100%;
}

.pep-type-form {
  display: inline-block;
  width: 100%;
  margin-top: 15px;
  margin-bottom: 2px;
}

.help {
  width: 18px;
  margin-top: -4px;
  cursor: pointer;
}

.according {
  margin-top: 5px;
  margin-bottom: 5px;
}

.invalid {
  border: 1px solid red;
  border-radius: 5px;
  padding: 2px;
}
</style>
